(function () {
  'use strict';

  var instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getScrollStep(section) {
    var firstSlide = section.querySelector('[data-product-short-video-slide]');
    var gap = parseFloat(getComputedStyle(section).getPropertyValue('--product-short-videos-gap')) || 10;
    return firstSlide ? firstSlide.getBoundingClientRect().width + gap : 180;
  }

  function getYouTubeId(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.origin);
      var host = url.hostname.replace(/^www\./, '');
      var path = url.pathname.split('/').filter(Boolean);

      if (host === 'youtu.be') return path[0] || '';
      if (path[0] === 'shorts' || path[0] === 'embed') return path[1] || '';

      return url.searchParams.get('v') || '';
    } catch (error) {
      return '';
    }
  }

  function getTikTokId(rawUrl) {
    var match = String(rawUrl || '').match(/\/video\/(\d+)/);
    return match ? match[1] : '';
  }

  function getInstagramEmbed(rawUrl) {
    try {
      var url = new URL(rawUrl, window.location.origin);
      var match = url.pathname.match(/\/(reel|p|tv)\/([^/?#]+)/);
      if (!match) return '';
      return 'https://www.instagram.com/' + match[1] + '/' + match[2] + '/embed';
    } catch (error) {
      return '';
    }
  }

  function isEmbedProvider(provider) {
    return provider === 'youtube' || provider === 'tiktok' || provider === 'instagram';
  }

  function canAutoplayProvider(provider) {
    return provider === 'mp4' || provider === 'youtube' || provider === 'tiktok';
  }

  function buildEmbedUrl(provider, rawUrl, options) {
    options = options || {};
    var shouldAutoplay = options.autoplay !== false;

    if (provider === 'youtube') {
      var youtubeId = getYouTubeId(rawUrl);
      if (!youtubeId) return '';

      var youtubeParams = new URLSearchParams({
        autoplay: shouldAutoplay ? '1' : '0',
        mute: shouldAutoplay ? '1' : '0',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
        iv_load_policy: '3',
        controls: options.controls === false ? '0' : '1',
        fs: options.inline ? '0' : '1'
      });

      return 'https://www.youtube.com/embed/' + youtubeId + '?' + youtubeParams.toString();
    }

    if (provider === 'tiktok') {
      var tiktokId = getTikTokId(rawUrl);
      if (!tiktokId) return '';

      var tiktokParams = new URLSearchParams({
        autoplay: shouldAutoplay ? '1' : '0',
        loop: '1',
        mute: '1',
        muted: '1',
        playsinline: '1',
        controls: '0',
        rel: '0',
        music_info: '0',
        description: '0',
        native_context_menu: '0',
        closed_caption: '0',
        play_button: '0',
        progress_bar: '0',
        volume_control: '0',
        fullscreen_button: '0'
      });

      return 'https://www.tiktok.com/player/v1/' + tiktokId + '?' + tiktokParams.toString();
    }

    if (provider === 'instagram') {
      return getInstagramEmbed(rawUrl);
    }

    return '';
  }

  function ProductShortVideos(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-product-short-videos-viewport]');
    this.track = section.querySelector('[data-product-short-videos-track]');
    this.modal = section.querySelector('[data-product-short-videos-modal]');
    this.modalMedia = section.querySelector('[data-product-short-videos-modal-media]');
    this.prevButton = section.querySelector('[data-product-short-videos-prev]');
    this.nextButton = section.querySelector('[data-product-short-videos-next]');
    this.hoverPlay = section.dataset.hoverPlay === 'true';
    this.mute = section.dataset.mute !== 'false';
    this.videoTitleFallback = section.dataset.videoTitleFallback || 'Video';
    this.resizeTimer = null;
    this.scrollRaf = null;
    this.scrollPlaybackTimer = null;
    this.defaultPlaybackTimer = null;
    this.defaultPlaybackIdle = null;
    this.defaultPlaybackTriggered = false;
    this.manualPlaybackPauseUntil = 0;
    this.defaultPlaybackObserver = null;
    this.lazyObserver = null;
    this.isDestroyed = false;
    this.drag = {
      down: false,
      startX: 0,
      scrollLeft: 0,
      moved: false,
      suppressClick: false
    };

    this.init();
  }

  ProductShortVideos.prototype.init = function () {
    this.updateColumns();
    this.setupCards();
    this.setupControls();
    this.setupDrag();
    this.setupModal();
    this.setupLazyLoading();
    this.setupDefaultPlayback();
    this.updateOverflowState();

    this.handleResize = this.onResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  };

  ProductShortVideos.prototype.onResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () {
      self.updateColumns();
      self.updateOverflowState();
      self.scheduleVisiblePlayback(160);
    }, 120);
  };

  ProductShortVideos.prototype.updateColumns = function () {
    var desktopCards = parseFloat(this.section.dataset.desktopCards || '3');
    var mobileCards = parseFloat(this.section.dataset.mobileCards || '1.65');
    var isMobile = window.innerWidth < 768;
    var columns = isMobile ? mobileCards : desktopCards;

    if (!columns || columns < 1) columns = isMobile ? 1.65 : 3;
    if (!isMobile) {
      columns = clamp(columns, 1, 3);
    } else {
      columns = clamp(Math.max(columns, window.innerWidth < 390 ? 1.55 : 1.65), 1.5, 2);
    }

    this.section.style.setProperty('--product-short-videos-columns', columns);
  };

  ProductShortVideos.prototype.updateOverflowState = function () {
    if (!this.viewport || !this.track) return;

    var hasOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
    this.section.classList.toggle('has-overflow', hasOverflow);
    this.track.classList.toggle('is-centered', !hasOverflow);

    var isMobile = window.innerWidth < 768;
    var controls = this.section.querySelector('[data-product-short-videos-controls]');
    if (controls) {
      controls.hidden = !hasOverflow || isMobile;
    }

    if (this.prevButton) {
      this.prevButton.hidden = !hasOverflow || isMobile || this.viewport.scrollLeft <= 2;
    }

    if (this.nextButton) {
      var atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 2;
      this.nextButton.hidden = !hasOverflow || isMobile || atEnd;
    }
  };

  ProductShortVideos.prototype.loadVideo = function (card) {
    var video = card && card.querySelector('video');
    if (!video) return null;

    if (!video.getAttribute('src') && video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      video.removeAttribute('data-src');
      video.load();
    }

    return video;
  };

  ProductShortVideos.prototype.createEmbedIframe = function (provider, rawUrl, title, options) {
    var embedUrl = buildEmbedUrl(provider, rawUrl, options);

    if (!embedUrl) return null;

    var iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.title = title || this.videoTitleFallback || 'Video';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.dataset.provider = provider;
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');

    return iframe;
  };

  ProductShortVideos.prototype.clearInlineEmbeds = function (exceptSlide) {
    this.section.querySelectorAll('[data-product-short-video-inline-frame]').forEach(function (iframe) {
      var slide = iframe.closest('[data-product-short-video-slide]');
      if (exceptSlide && slide === exceptSlide) return;

      var card = slide && slide.querySelector('[data-product-short-video-card]');
      var playButton = card && card.querySelector('.social-video-card__direct-play-button');

      iframe.remove();

      if (card) {
        card.classList.remove('playing', 'is-embed-playing');
      }

      if (playButton) {
        playButton.classList.remove('is-playing');
      }
    });
  };

  ProductShortVideos.prototype.pauseInlineVideos = function (exceptVideo, exceptSlide) {
    this.section.querySelectorAll('.product-short-videos__video').forEach(function (video) {
      if (video === exceptVideo) return;

      video.pause();
      var card = video.closest('[data-product-short-video-card]');
      if (card) {
        card.classList.remove('playing');
        var playButton = card.querySelector('.social-video-card__direct-play-button');
        if (playButton) playButton.classList.remove('is-playing');
      }
    });

    this.clearInlineEmbeds(exceptSlide || null);
  };

  ProductShortVideos.prototype.toggleInlineVideo = function (card, slide) {
    var video = this.loadVideo(card);
    if (!video) return;
    var wasPaused = video.paused;

    this.pauseInlineVideos(video, slide || null);

    if (wasPaused) {
      this.manualPlaybackPauseUntil = 0;
      video.muted = true;
      video.defaultMuted = true;
      video.play().catch(function () {});
    } else {
      this.manualPlaybackPauseUntil = Date.now() + 1600;
      video.pause();
    }
  };

  ProductShortVideos.prototype.playInlineEmbed = function (slide, options) {
    if (!slide || !isEmbedProvider(slide.dataset.provider)) return false;

    options = options || {};

    var card = slide.querySelector('[data-product-short-video-card]');
    var media = slide.querySelector('.product-short-videos__media');
    var existingIframe = slide.querySelector('[data-product-short-video-inline-frame]');

    if (!card || !media) return false;

    if (existingIframe) {
      if (options.auto) return true;

      this.manualPlaybackPauseUntil = Date.now() + 1600;
      existingIframe.remove();
      card.classList.remove('playing', 'is-embed-playing');
      var currentPlayButton = card.querySelector('.social-video-card__direct-play-button');
      if (currentPlayButton) currentPlayButton.classList.remove('is-playing');
      return false;
    }

    var provider = slide.dataset.provider;
    var rawUrl = slide.dataset.videoUrl || '';
    var title = slide.dataset.videoTitle || this.videoTitleFallback || 'Video';
    var iframe = this.createEmbedIframe(provider, rawUrl, title, {
      inline: true,
      autoplay: true,
      controls: provider !== 'tiktok'
    });

    if (!iframe) return false;

    iframe.className = 'product-short-videos__inline-frame';
    iframe.setAttribute('data-product-short-video-inline-frame', '');

    if (!options.auto) this.manualPlaybackPauseUntil = 0;
    this.pauseInlineVideos(null, slide);
    media.appendChild(iframe);

    card.classList.add('playing', 'is-embed-playing');
    var playButton = card.querySelector('.social-video-card__direct-play-button');
    if (playButton) playButton.classList.add('is-playing');

    return true;
  };

  ProductShortVideos.prototype.playSlideInline = function (slide, options) {
    if (!slide) return false;

    options = options || {};
    if (options.auto && Date.now() < this.manualPlaybackPauseUntil) return false;

    var provider = slide.dataset.provider;
    var card = slide.querySelector('[data-product-short-video-card]');

    if (provider === 'mp4') {
      var video = this.loadVideo(card);
      if (!video) return false;

      video.muted = true;
      video.defaultMuted = true;
      this.pauseInlineVideos(video, slide);
      this.defaultPlaybackTriggered = true;
      video.play().catch(function () {});

      return true;
    }

    if (isEmbedProvider(provider)) {
      return this.playInlineEmbed(slide, options);
    }

    return false;
  };

  ProductShortVideos.prototype.openModal = function (slide, sourceVideo) {
    if (!this.modal || !this.modalMedia || !slide) return;

    var provider = slide.dataset.provider;
    var videoSrc = slide.dataset.videoSrc || '';
    var rawUrl = slide.dataset.videoUrl || '';
    var title = slide.dataset.videoTitle || this.videoTitleFallback || 'Video';

    this.pauseInlineVideos(sourceVideo || null);
    this.modalMedia.innerHTML = '';

    if (provider === 'mp4' && videoSrc) {
      var video = document.createElement('video');
      video.src = videoSrc;
      video.controls = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.muted = this.mute;
      this.modalMedia.appendChild(video);
      video.play().catch(function () {});
    } else {
      var iframe = this.createEmbedIframe(provider, rawUrl, title, {
        inline: false,
        autoplay: true,
        controls: provider !== 'tiktok'
      });

      if (!iframe) return;

      this.modalMedia.appendChild(iframe);
    }

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    if (this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }
    document.body.style.overflow = 'hidden';

    if (sourceVideo) {
      sourceVideo.pause();
    }
  };

  ProductShortVideos.prototype.closeModal = function () {
    if (!this.modal || !this.modalMedia) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modalMedia.innerHTML = '';
    document.body.style.overflow = '';
  };

  ProductShortVideos.prototype.setupCards = function () {
    var self = this;

    this.section.querySelectorAll('[data-product-short-video-card]').forEach(function (card) {
      var slide = card.closest('[data-product-short-video-slide]');
      var provider = slide ? slide.dataset.provider : '';
      var fullscreenButton = card.querySelector('[data-product-short-video-fullscreen]');
      var playButton = card.querySelector('.social-video-card__direct-play-button');
      var video = card.querySelector('video');

      card.addEventListener('click', function (event) {
        if (event.target.closest('a') || event.target.closest('[data-product-short-video-fullscreen]')) return;
        if (!slide) return;

        event.preventDefault();

        if (provider === 'mp4') {
          self.toggleInlineVideo(card, slide);
        } else if (isEmbedProvider(provider)) {
          self.playInlineEmbed(slide);
        } else {
          self.openModal(slide, video || null);
        }
      });

      card.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        if (provider === 'mp4') {
          self.toggleInlineVideo(card, slide);
        } else if (isEmbedProvider(provider)) {
          self.playInlineEmbed(slide);
        } else {
          self.openModal(slide, video || null);
        }
      });

      if (fullscreenButton) {
        fullscreenButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.openModal(slide, video || null);
        });
      }

      if (video) {
        video.addEventListener('play', function () {
          card.classList.add('playing');
          if (playButton) playButton.classList.add('is-playing');
        });

        video.addEventListener('pause', function () {
          card.classList.remove('playing');
          if (playButton) playButton.classList.remove('is-playing');
        });

        video.addEventListener('ended', function () {
          card.classList.remove('playing');
          if (playButton) playButton.classList.remove('is-playing');
        });
      }

      if (self.hoverPlay && provider === 'mp4') {
        card.addEventListener('mouseenter', function () {
          if (window.innerWidth < 768) return;
          var loadedVideo = self.loadVideo(card);
          if (!loadedVideo) return;

          self.manualPlaybackPauseUntil = 0;
          loadedVideo.muted = true;
          loadedVideo.defaultMuted = true;
          self.pauseInlineVideos(loadedVideo, slide);
          loadedVideo.play().catch(function () {});
        });
      }
    });
  };

  ProductShortVideos.prototype.setupControls = function () {
    var self = this;
    if (!this.viewport) return;

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.viewport.scrollBy({ left: -getScrollStep(self.section), behavior: 'smooth' });
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.viewport.scrollBy({ left: getScrollStep(self.section), behavior: 'smooth' });
      });
    }

    this.viewport.addEventListener('scroll', function () {
      if (!self.scrollRaf) {
        self.scrollRaf = window.requestAnimationFrame(function () {
          self.scrollRaf = null;
          self.updateOverflowState();
        });
      }

      self.scheduleVisiblePlayback();
    }, { passive: true });

    this.viewport.addEventListener('touchend', function () {
      self.scheduleVisiblePlayback(220);
    }, { passive: true });

    this.viewport.addEventListener('scrollend', function () {
      self.scheduleVisiblePlayback(40);
    }, { passive: true });
  };

  ProductShortVideos.prototype.scheduleVisiblePlayback = function (delay) {
    var self = this;

    clearTimeout(this.scrollPlaybackTimer);
    this.scrollPlaybackTimer = window.setTimeout(function () {
      self.scrollPlaybackTimer = null;
      if (Date.now() < self.manualPlaybackPauseUntil) return;
      self.playMostVisibleVideo();
    }, typeof delay === 'number' ? delay : 180);
  };

  ProductShortVideos.prototype.playMostVisibleVideo = function () {
    if (!this.viewport) return false;
    if (Date.now() < this.manualPlaybackPauseUntil) return false;

    var viewportRect = this.viewport.getBoundingClientRect();
    var bestSlide = null;
    var bestRatio = 0;

    this.section.querySelectorAll('[data-product-short-video-slide]').forEach(function (slide) {
      if (!canAutoplayProvider(slide.dataset.provider)) return;

      var rect = slide.getBoundingClientRect();
      var visibleWidth = Math.max(0, Math.min(rect.right, viewportRect.right) - Math.max(rect.left, viewportRect.left));
      var visibleHeight = Math.max(0, Math.min(rect.bottom, viewportRect.bottom) - Math.max(rect.top, viewportRect.top));
      var area = Math.max(1, rect.width * rect.height);
      var ratio = (visibleWidth * visibleHeight) / area;

      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestSlide = slide;
      }
    });

    if (!bestSlide || bestRatio < 0.35) return false;

    return this.playSlideInline(bestSlide, { auto: true });
  };

  ProductShortVideos.prototype.scheduleDefaultPlayback = function (delay) {
    var self = this;

    if (this.defaultPlaybackTriggered) return;

    clearTimeout(this.defaultPlaybackTimer);
    if (this.defaultPlaybackIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.defaultPlaybackIdle);
      this.defaultPlaybackIdle = null;
    }

    this.defaultPlaybackTimer = window.setTimeout(function () {
      self.defaultPlaybackTimer = null;
      if (self.isDestroyed || self.defaultPlaybackTriggered) return;

      if ('requestIdleCallback' in window) {
        self.defaultPlaybackIdle = window.requestIdleCallback(function () {
          self.defaultPlaybackIdle = null;
          if (!self.isDestroyed) self.playFirstVideo();
        }, { timeout: 1200 });
      } else {
        self.playFirstVideo();
      }
    }, typeof delay === 'number' ? delay : 500);
  };

  ProductShortVideos.prototype.setupDrag = function () {
    var self = this;
    var viewport = this.viewport;
    if (!viewport) return;

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;

      self.drag.down = true;
      self.drag.startX = event.clientX;
      self.drag.scrollLeft = viewport.scrollLeft;
      self.drag.moved = false;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (event.pointerType === 'touch') return;
      if (!self.drag.down) return;

      var delta = event.clientX - self.drag.startX;
      if (Math.abs(delta) > 4) {
        self.drag.moved = true;
        viewport.classList.add('is-dragging');
        viewport.scrollLeft = self.drag.scrollLeft - delta;
        event.preventDefault();
      }
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (eventName) {
      viewport.addEventListener(eventName, function () {
        if (!self.drag.down) return;

        self.drag.down = false;
        viewport.classList.remove('is-dragging');
        self.drag.suppressClick = self.drag.moved;
        if (self.drag.moved) self.scheduleVisiblePlayback(80);
        self.drag.moved = false;
      });
    });

    viewport.addEventListener('click', function (event) {
      if (!self.drag.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      self.drag.suppressClick = false;
    }, true);
  };

  ProductShortVideos.prototype.setupLazyLoading = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) return;

    this.lazyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var card = entry.target.querySelector('[data-product-short-video-card]');
        if (card) self.loadVideo(card);
        self.lazyObserver.unobserve(entry.target);
      });
    }, {
      root: this.viewport || null,
      rootMargin: '0px 80px',
      threshold: 0.01
    });

    this.section.querySelectorAll('[data-provider="mp4"]').forEach(function (slide) {
      self.lazyObserver.observe(slide);
    });
  };

  ProductShortVideos.prototype.setupDefaultPlayback = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      this.scheduleDefaultPlayback(600);
      return;
    }

    this.defaultPlaybackObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        self.scheduleDefaultPlayback(500);
        if (self.defaultPlaybackObserver) self.defaultPlaybackObserver.unobserve(self.section);
      });
    }, {
      threshold: 0.25
    });

    this.defaultPlaybackObserver.observe(this.section);
  };

  ProductShortVideos.prototype.playFirstVideo = function () {
    if (this.defaultPlaybackTriggered) return false;

    var firstSlide = this.section.querySelector('[data-product-short-video-slide]');
    if (!firstSlide || !canAutoplayProvider(firstSlide.dataset.provider)) return false;

    return this.playSlideInline(firstSlide, { auto: true });
  };

  ProductShortVideos.prototype.setupModal = function () {
    var self = this;
    var closeButton = this.section.querySelector('[data-product-short-videos-close]');

    if (!this.modal) return;

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        self.closeModal();
      });
    }

    this.modal.addEventListener('click', function (event) {
      if (event.target === self.modal) {
        self.closeModal();
      }
    });

    this.handleEscape = function (event) {
      if (event.key === 'Escape' && self.modal.classList.contains('is-open')) {
        self.closeModal();
      }
    };

    document.addEventListener('keydown', this.handleEscape);
  };

  ProductShortVideos.prototype.destroy = function () {
    this.isDestroyed = true;
    this.clearInlineEmbeds(null);
    window.removeEventListener('resize', this.handleResize);
    if (this.handleEscape) document.removeEventListener('keydown', this.handleEscape);
    if (this.defaultPlaybackObserver) this.defaultPlaybackObserver.disconnect();
    if (this.lazyObserver) this.lazyObserver.disconnect();
    if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.scrollPlaybackTimer);
    clearTimeout(this.defaultPlaybackTimer);
    if (this.defaultPlaybackIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.defaultPlaybackIdle);
    }
  };

  function initAll() {
    document.querySelectorAll('[data-product-short-videos]').forEach(function (section) {
      if (!instances.has(section)) {
        instances.set(section, new ProductShortVideos(section));
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target && event.target.querySelector && event.target.querySelector('[data-product-short-videos]');
    if (!section || !instances.has(section)) return;

    instances.get(section).destroy();
    instances.delete(section);
  });
})();
